import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Handshake, Wrench, Heart, Phone, EnvelopeSimple, Tag } from "@phosphor-icons/react";
import { BUSINESS } from "@/data/site";

const ABOUT_IMG = "https://images.pexels.com/photos/38190070/pexels-photo-38190070.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUES = [
  { icon: ShieldCheck, title: "Reliability", desc: "We arrive on time, prepared, and ready to work." },
  { icon: Handshake, title: "Clear communication", desc: "Transparent estimates and honest updates — start to finish." },
  { icon: Wrench, title: "Attention to detail", desc: "Quality workmanship on every job — big or small." },
  { icon: Heart, title: "Respect for your property", desc: "We treat your home or business like it's our own." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="ji-container pt-16 md:pt-24 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="ji-overline mb-5">About Joseph Industries LLC</div>
          <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight">
            Reliable. Professional.<br />
            <span className="text-[var(--ji-accent)] italic">Built on quality.</span>
          </h1>
          <p className="mt-7 text-lg text-[var(--ji-muted)] leading-relaxed max-w-xl">
            At Joseph Industries LLC, we provide dependable, professional home and commercial services for customers throughout Southern Louisiana. Homeowners, landlords, property managers, contractors, real estate professionals, and businesses trust our team to deliver quality workmanship, honest pricing, and dependable service on every job.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="overflow-hidden border border-[var(--ji-border)] ji-noise" style={{ borderRadius: 4 }}>
            <img src={ABOUT_IMG} alt="Joseph Industries — technician on the job" loading="eager" fetchpriority="high" decoding="async" className="w-full h-[420px] md:h-[520px] object-cover" data-testid="about-image" />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="ji-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="ji-overline mb-4">Our Standard</div>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight">
              Prompt scheduling.<br />Transparent estimates.<br />Lasting results.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-lg text-[var(--ji-muted)] leading-relaxed">
            <p>
              Our reputation is built on reliability, clear communication, and attention to detail.
              Whether you need an appliance repaired, a new appliance professionally installed,
              post-construction cleanup completed on schedule, junk and debris removed, or
              time-sensitive courier services, we arrive prepared to get the job done safely,
              efficiently, and with lasting results.
            </p>
            <p>
              We understand that your time is valuable. That's why we prioritize prompt scheduling,
              transparent estimates, and professional service from start to finish. Every project
              is completed with respect for your property, a commitment to quality, and a focus
              on customer satisfaction.
            </p>
            <p className="inline-flex items-center gap-2 text-[var(--ji-accent)] font-semibold not-italic">
              <Tag size={16} weight="fill" /> Free quotes and estimates on every service.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[var(--ji-surface-alt)] border-y border-[var(--ji-border)]">
        <div className="ji-container py-20 md:py-24">
          <div className="ji-overline mb-4">What we stand on</div>
          <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            Values that show up in the work.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="ji-card p-7" style={{ borderRadius: 4 }} data-testid={`value-${i}`}>
                  <span className="inline-flex w-12 h-12 items-center justify-center bg-[var(--ji-surface-alt)] text-[var(--ji-primary)]" style={{ borderRadius: 4 }}>
                    <Icon size={24} weight="duotone" />
                  </span>
                  <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-5 text-xl font-bold tracking-tight">{v.title}</h3>
                  <p className="mt-2 text-sm text-[var(--ji-muted)] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Direct contact */}
      <section className="ji-container py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid="about-cta-call" className="ji-card p-8 flex items-center justify-between group" style={{ borderRadius: 4 }}>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)]">{BUSINESS.primaryPhone.label}</div>
              <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold tracking-tight mt-1">{BUSINESS.primaryPhone.display}</div>
            </div>
            <Phone size={28} weight="bold" className="text-[var(--ji-primary)]" />
          </a>
          <a href={`mailto:${BUSINESS.primaryEmail}`} data-testid="about-cta-email" className="ji-card p-8 flex items-center justify-between group" style={{ borderRadius: 4 }}>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)]">Email David</div>
              <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl md:text-2xl font-bold tracking-tight mt-1 break-all">{BUSINESS.primaryEmail}</div>
            </div>
            <EnvelopeSimple size={28} weight="bold" className="text-[var(--ji-primary)]" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="ji-container pb-24">
        <div className="ji-card p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6" style={{ borderRadius: 4 }}>
          <div>
            <div className="ji-overline mb-3">Ready when you are</div>
            <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl md:text-4xl font-bold tracking-tight">
              Reliable. Professional. Built on quality.
            </h3>
            <p className="mt-3 text-[var(--ji-muted)]">Serving Southern Louisiana from New Orleans to Baton Rouge and every parish in between.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact" data-testid="about-cta-request" className="ji-btn-accent inline-flex items-center gap-2 px-6 h-14 text-base font-semibold" style={{ borderRadius: 4 }}>
              Get a Free Quote <ArrowRight size={18} weight="bold" />
            </Link>
            <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid="about-cta-call2" className="ji-btn-primary inline-flex items-center gap-2 px-6 h-14 text-base font-semibold" style={{ borderRadius: 4 }}>
              Call {BUSINESS.primaryPhone.display} <ArrowRight size={18} weight="bold" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
